<template>
  <RevLoadingScreen
    v-if="!offer"
    class="my-auto"
    text="Your offer is loading"
  />
  <template v-else>
    <div class="mx-auto my-0 max-w-[34rem] p-24">
      <p class="body-1 mb-32 h-56 border-b py-16 text-center">
        {{ i18n(translations.headerTitle) }}
      </p>
      <h1 class="heading-1 mb-16">
        {{ i18n(translations.punchline) }}
      </h1>
      <p class="body-1 mb-16" data-qa="offer" data-test="offer">
        <FormattedMessage :definition="translations.offer">
          <template #amount>
            <span class="body-1-bold">
              {{ offerPrice }}
            </span>
          </template>
          <template #model>
            <span class="body-1-bold">
              {{ offer?.product.fullTitle }}
            </span>
          </template>
        </FormattedMessage>
      </p>
      <div
        class="mx-auto my-0 hidden max-w-[34rem] text-right md:mx-0 md:mb-24 md:mt-16 md:block"
      >
        <RevButton data-qa="decline" variant="secondary" @click="declineOffer">
          {{ i18n(translations.decline) }}
        </RevButton>
        <RevButton
          class="ml-16"
          data-qa="accept"
          variant="primary"
          @click="acceptOffer"
        >
          {{ i18n(translations.accept) }}
        </RevButton>
      </div>
      <OfferSummary class="mb-24" :summary="offer?.funnelSummary" />
      <p class="body-1-bold mb-16">
        {{ i18n(translations.nextStep) }}
      </p>
      <div
        v-for="wording in offer?.instructions"
        :key="wording.title"
        class="mb-16 flex"
      >
        <div>
          <IconCheckInCircle />
        </div>
        <div class="ml-16">
          <p class="body-1">
            {{ wording.title }}
          </p>
          <p
            v-for="paragraph in wording.paragraphs"
            :key="paragraph"
            class="body-2"
          >
            {{ paragraph }}
          </p>
        </div>
      </div>
      <span class="my-32 block h-1 w-full" />
      <p class="body-1-bold mb-16">
        {{ i18n(translations.lastThing) }}
      </p>
      <p class="body-1">
        {{ i18n(translations.lastThingParagraph) }}
      </p>
      <div
        class="mx-auto my-0 hidden max-w-[34rem] text-right md:mx-0 md:mb-24 md:mt-16 md:block"
      >
        <RevButton data-qa="decline" variant="secondary" @click="declineOffer">
          {{ i18n(translations.decline) }}
        </RevButton>
        <RevButton
          class="ml-16"
          data-qa="accept"
          variant="primary"
          @click="acceptOffer"
        >
          {{ i18n(translations.accept) }}
        </RevButton>
      </div>
    </div>
    <RevStickyBar class="md:hidden">
      <div
        class="mx-auto my-0 flex max-w-[34rem] text-right md:mx-0 md:mb-24 md:mt-16"
      >
        <RevButton
          data-qa="decline"
          full-width="always"
          variant="secondary"
          @click="declineOffer"
        >
          {{ i18n(translations.decline) }}
        </RevButton>
        <RevButton
          class="ml-16"
          data-qa="accept"
          full-width="always"
          variant="primary"
          @click="acceptOffer"
        >
          {{ i18n(translations.accept) }}
        </RevButton>
      </div>
    </RevStickyBar>
  </template>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { computed, inject, onMounted, ref } from 'vue'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { omit } from '@backmarket/utils/object/omit'
import { RevButton } from '@ds/components/Button'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevStickyBar } from '@ds/components/StickyBar'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'

import { useBuybackFunnelOffer } from '~/scopes/buyback/composables/useBuybackFunnelOffer'
import { FUNNEL_ERROR_MESSAGE } from '~/scopes/buyback/pages/constants'
import { CMS } from '~/scopes/cms/routes-names'

import OfferSummary from '../components/OfferSummary/OfferSummary.vue'
import type { ErrorData } from '../components/TheCatcher/useCatcher'
import { ROUTE_NAMES } from '../constants'

import translations from './Offer.translations'

const i18n = useI18n()
const route = useRoute()
const { trackClick } = useTracking()
const { offer } = useBuybackFunnelOffer()

const getOfferPayload = omit(route.query, ['nextStep', 'listingPublicId'])
const errorData = inject<ErrorData>('errorData', ref({ message: '', step: '' }))
errorData.value = {
  message: FUNNEL_ERROR_MESSAGE.BUYBACK_FUNNEL,
  step: 'Offer',
}

const offerPrice = computed(() => {
  return offer?.value?.listing.price
    ? i18n.price(offer.value.listing.price)
    : ''
})

onMounted(async () => {
  if (!offer.value) {
    await navigateTo({
      name: CMS.BUYBACK,
      params: {
        pageName: 'home',
      },
    })
  }
})

const nextStep = computed(() => {
  const query = route?.query?.partner
    ? {
        partner: route.query.partner,
      }
    : {}

  return offer.value?.bankDetailsRequired
    ? {
        name: ROUTE_NAMES.BANK,
        params: route.params,
        query,
      }
    : {
        name: ROUTE_NAMES.ADDRESS,
        params: route.params,
        query,
      }
})

const trackAction = (name: string) => {
  trackClick({
    zone: 'buyback',
    name,
    value: {
      ...getOfferPayload,
      listing_public_id: route.params.listingPublicId || '',
      estimation: offer.value?.listing.price.amount.toString(),
    },
  })
}

async function acceptOffer() {
  trackAction('accept_offer')
  await navigateTo(nextStep.value)
}

async function declineOffer() {
  trackAction('decline_offer')
  await navigateTo({
    name: CMS.BUYBACK,
    params: {
      pageName: 'home',
    },
  })
}
</script>
